import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, 
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  Tooltip, Alert, CircularProgress, Divider, Paper, List,
  ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Wifi as WifiIcon,
  Videocam as CameraIcon,
  Save as SaveIcon,
  PlayArrow as TestIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AutoAwesome as AutoIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosConfig';
import UniFiSetupWizard from './UniFiSetupWizard';

const UniFiSettings = () => {
  const [settings, setSettings] = useState({
    network: {
      host: '',
      port: 443,
      username: '',
      password: '',
      site: 'default'
    },
    protect: {
      host: '',
      port: 443,
      username: '',
      password: ''
    },
    access: {
      host: '',
      port: 443,
      username: '',
      password: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [showPasswords, setShowPasswords] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/unifi/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axiosInstance.post('/api/unifi/settings', settings);
      showSnackbar('Configuración guardada exitosamente', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar('Error al guardar configuración', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (service) => {
    try {
      setTesting(true);
      const response = await axiosInstance.post('/api/unifi/test-connection', {
        service,
        config: settings[service]
      });
      setTestResults(prev => ({
        ...prev,
        [service]: response.data
      }));
      showSnackbar(`Prueba de ${service} completada`, 'success');
    } catch (error) {
      console.error(`Error testing ${service}:`, error);
      setTestResults(prev => ({
        ...prev,
        [service]: { success: false, message: 'Error de conexión' }
      }));
      showSnackbar(`Error al probar ${service}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleUpdateSetting = (service, field, value) => {
    setSettings(prev => ({
      ...prev,
      [service]: {
        ...prev[service],
        [field]: value
      }
    }));
  };

  const handleSetupWizardComplete = (newConfig) => {
    setSettings(newConfig);
    setShowSetupWizard(false);
    showSnackbar('Configuración UniFi completada exitosamente', 'success');
    fetchSettings(); // Refresh settings
  };

  const showSnackbar = (message, severity = 'success') => {
    console.log(`${severity}: ${message}`);
  };

  const getTestResultIcon = (result) => {
    if (!result) return null;
    return result.success ? <SuccessIcon color="success" /> : <ErrorIcon color="error" />;
  };

  const getTestResultColor = (result) => {
    if (!result) return 'default';
    return result.success ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Configuración UniFi
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AutoIcon />}
            onClick={() => setShowSetupWizard(true)}
            color="primary"
          >
            Configuración Rápida
          </Button>
          <Button
            variant="outlined"
            startIcon={<TestIcon />}
            onClick={() => {
              handleTestConnection('network');
              handleTestConnection('protect');
              handleTestConnection('access');
            }}
            disabled={testing}
          >
            Probar Todas las Conexiones
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
          >
            Guardar Configuración
          </Button>
        </Box>
      </Box>

      {/* Quick Setup Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>¿Primera vez configurando UniFi?</strong> Use la "Configuración Rápida" para un setup automático y guiado.
        </Typography>
      </Alert>

      {/* Connection Status */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WifiIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">UniFi Network</Typography>
              <Chip
                icon={getTestResultIcon(testResults.network)}
                label={testResults.network?.success ? 'Conectado' : 'Desconectado'}
                color={getTestResultColor(testResults.network)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CameraIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">UniFi Protect</Typography>
              <Chip
                icon={getTestResultIcon(testResults.protect)}
                label={testResults.protect?.success ? 'Conectado' : 'Desconectado'}
                color={getTestResultColor(testResults.protect)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SecurityIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6">UniFi Access</Typography>
              <Chip
                icon={getTestResultIcon(testResults.access)}
                label={testResults.access?.success ? 'Conectado' : 'Desconectado'}
                color={getTestResultColor(testResults.access)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Settings Accordion */}
      <Paper sx={{ mb: 3 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WifiIcon color="primary" />
              <Typography variant="h6">UniFi Network</Typography>
              <Chip
                icon={getTestResultIcon(testResults.network)}
                label="Prueba"
                color={getTestResultColor(testResults.network)}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Host/IP"
                  value={settings.network.host}
                  onChange={(e) => handleUpdateSetting('network', 'host', e.target.value)}
                  fullWidth
                  placeholder="192.168.1.1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Puerto"
                  type="number"
                  value={settings.network.port}
                  onChange={(e) => handleUpdateSetting('network', 'port', parseInt(e.target.value))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Usuario"
                  value={settings.network.username}
                  onChange={(e) => handleUpdateSetting('network', 'username', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contraseña"
                  type={showPasswords ? 'text' : 'password'}
                  value={settings.network.password}
                  onChange={(e) => handleUpdateSetting('network', 'password', e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPasswords(!showPasswords)}
                        edge="end"
                      >
                        {showPasswords ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Sitio"
                  value={settings.network.site}
                  onChange={(e) => handleUpdateSetting('network', 'site', e.target.value)}
                  fullWidth
                  placeholder="default"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  startIcon={<TestIcon />}
                  onClick={() => handleTestConnection('network')}
                  disabled={testing}
                  fullWidth
                >
                  Probar Conexión
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CameraIcon color="info" />
              <Typography variant="h6">UniFi Protect</Typography>
              <Chip
                icon={getTestResultIcon(testResults.protect)}
                label="Prueba"
                color={getTestResultColor(testResults.protect)}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Host/IP"
                  value={settings.protect.host}
                  onChange={(e) => handleUpdateSetting('protect', 'host', e.target.value)}
                  fullWidth
                  placeholder="192.168.1.1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Puerto"
                  type="number"
                  value={settings.protect.port}
                  onChange={(e) => handleUpdateSetting('protect', 'port', parseInt(e.target.value))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Usuario"
                  value={settings.protect.username}
                  onChange={(e) => handleUpdateSetting('protect', 'username', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contraseña"
                  type={showPasswords ? 'text' : 'password'}
                  value={settings.protect.password}
                  onChange={(e) => handleUpdateSetting('protect', 'password', e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPasswords(!showPasswords)}
                        edge="end"
                      >
                        {showPasswords ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<TestIcon />}
                  onClick={() => handleTestConnection('protect')}
                  disabled={testing}
                  fullWidth
                >
                  Probar Conexión
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SecurityIcon color="success" />
              <Typography variant="h6">UniFi Access</Typography>
              <Chip
                icon={getTestResultIcon(testResults.access)}
                label="Prueba"
                color={getTestResultColor(testResults.access)}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Host/IP"
                  value={settings.access.host}
                  onChange={(e) => handleUpdateSetting('access', 'host', e.target.value)}
                  fullWidth
                  placeholder="192.168.1.1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Puerto"
                  type="number"
                  value={settings.access.port}
                  onChange={(e) => handleUpdateSetting('access', 'port', parseInt(e.target.value))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Usuario"
                  value={settings.access.username}
                  onChange={(e) => handleUpdateSetting('access', 'username', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contraseña"
                  type={showPasswords ? 'text' : 'password'}
                  value={settings.access.password}
                  onChange={(e) => handleUpdateSetting('access', 'password', e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPasswords(!showPasswords)}
                        edge="end"
                      >
                        {showPasswords ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<TestIcon />}
                  onClick={() => handleTestConnection('access')}
                  disabled={testing}
                  fullWidth
                >
                  Probar Conexión
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Advanced Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Configuración Avanzada
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>Sincronización automática</Typography>
                <Switch defaultChecked />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>Logs detallados</Typography>
                <Switch defaultChecked />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>Notificaciones push</Typography>
                <Switch />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography>Modo de desarrollo</Typography>
                <Switch />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Test Results */}
      {Object.keys(testResults).length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Resultados de Pruebas
            </Typography>
            <List>
              {Object.entries(testResults).map(([service, result]) => (
                <ListItem key={service}>
                  <ListItemIcon>
                    {getTestResultIcon(result)}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${service.charAt(0).toUpperCase() + service.slice(1)}`}
                    secondary={result?.message || 'Sin mensaje'}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={result?.success ? 'Exitoso' : 'Fallido'}
                      color={getTestResultColor(result)}
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Setup Wizard Dialog */}
      <Dialog 
        open={showSetupWizard} 
        onClose={() => setShowSetupWizard(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <UniFiSetupWizard
            onComplete={handleSetupWizardComplete}
            onCancel={() => setShowSetupWizard(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UniFiSettings; 